import { useEffect, useState, useCallback } from 'react'
import { Unlock, Check, X, Banknote } from 'lucide-react'
import api from '../api/client'
import { confirmModal, successModal, errorModal } from '../components/ui'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const dt = (s) => (s ? new Date(s).toLocaleString('pt-BR') : '—')
const mesAnterior = () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 7) }

export default function FinanceiroAdmin() {
  const [comp, setComp] = useState(mesAnterior())
  const [saques, setSaques] = useState([])
  const [filtro, setFiltro] = useState('em_analise')

  const load = useCallback(() => {
    const qs = filtro ? `?status=${filtro}` : ''
    api.get(`/financeiro/saques${qs}`).then((r) => setSaques(r.data.data || [])).catch(() => {})
  }, [filtro])
  useEffect(() => { load() }, [load])

  async function liberar() {
    if (!(await confirmModal(`Liberar as comissões da competência ${comp}? Elas entrarão no saldo disponível das carteiras.`, { title: 'Fechamento / Liberação', okLabel: 'Liberar' }))) return
    try { const r = await api.post('/financeiro/liberar', { competencia: comp }); successModal(`${r.data.comissoes} comissões liberadas (${money(r.data.total)}) para ${r.data.licenciados} licenciados.`, { title: 'Liberado' }); load() }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }
  async function aprovar(s) { try { await api.post(`/financeiro/saques/${s.id}/aprovar`); load() } catch (e) { errorModal(e?.response?.data?.detail) } }
  async function pagar(s) {
    if (!(await confirmModal(`Confirmar PAGAMENTO do saque de ${s.licenciado_nome || '—'} (${money(s.valor)})?`, { title: 'Pagar saque', okLabel: 'Pagar' }))) return
    try { await api.post(`/financeiro/saques/${s.id}/pagar`); successModal('Saque pago.'); load() } catch (e) { errorModal(e?.response?.data?.detail) }
  }
  async function rejeitar(s) {
    const motivo = window.prompt('Motivo da rejeição:')
    if (!motivo) return
    try { await api.post(`/financeiro/saques/${s.id}/rejeitar`, { motivo }); successModal('Saque rejeitado, saldo devolvido.'); load() } catch (e) { errorModal(e?.response?.data?.detail) }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Tesouraria · Liberação & Saques</h1>
        <p className="text-sm text-gray-500 mt-0.5">Fechamento mensal das comissões e processamento dos saques dos licenciados.</p>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Competência (AAAA-MM)</label>
          <input value={comp} onChange={(e) => setComp(e.target.value)} placeholder="2026-06" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <button onClick={liberar} className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 text-sm font-semibold shadow"><Unlock size={16} /> Liberar comissões</button>
        <p className="text-xs text-gray-400">Prévia → Liberada (entra no saldo disponível para saque).</p>
      </div>

      <div className="flex items-center gap-2">
        {['em_analise', 'aprovado', 'pago', 'rejeitado', ''].map((s) => (
          <button key={s || 'todos'} onClick={() => setFiltro(s)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filtro === s ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>{s || 'todos'}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">Solicitado</th><th className="px-4 py-3">Licenciado</th><th className="px-4 py-3 text-right">Valor</th><th className="px-4 py-3">Chave Pix</th><th className="px-4 py-3">NF</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Ações</th></tr>
          </thead>
          <tbody>
            {saques.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 hover:bg-primary-50/30">
                <td className="px-4 py-2.5 text-gray-500">{dt(s.solicitado_em)}</td>
                <td className="px-4 py-2.5 font-medium">{s.licenciado_nome || `#${s.licenciado_id}`}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{money(s.valor)}</td>
                <td className="px-4 py-2.5 text-gray-500">{s.chave_pix}</td>
                <td className="px-4 py-2.5 text-gray-500">{s.nf_numero || (s.tem_nf ? 'anexada' : '—')}</td>
                <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${s.status === 'pago' ? 'bg-blue-100 text-blue-700' : s.status === 'rejeitado' ? 'bg-rose-100 text-rose-700' : s.status === 'aprovado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{s.status}</span></td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    {s.status === 'em_analise' && <button onClick={() => aprovar(s)} title="Aprovar" className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"><Check size={15} /></button>}
                    {(s.status === 'aprovado' || s.status === 'em_analise') && <button onClick={() => pagar(s)} title="Pagar" className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"><Banknote size={15} /></button>}
                    {(s.status === 'em_analise' || s.status === 'aprovado') && <button onClick={() => rejeitar(s)} title="Rejeitar" className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50"><X size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {saques.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Nenhum saque.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}
